import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SidebarSettingsLayout from "../components/Shared/SidebarSettingsLayout";
import BookingPageForm from "../components/BookingLinkConfig/General/BookingPageForm";
import BusinessIdentityForm from "../components/BookingLinkConfig/Identity/BusinessIdentityForm";
import PersonalIdentityForm from "../components/BookingLinkConfig/Identity/PersonalIdentityForm";
import ContactInformationForm from "../components/BookingLinkConfig/General/ContactInformationForm";
import SocialMediaForm from "../components/BookingLinkConfig/General/SocialMediaForm";
import VisualDesignForm from "../components/BookingLinkConfig/Branding/VisualDesignForm";
import CancellationPolicyForm from "../components/BookingLinkConfig/Policies/CancellationPolicyForm";
import LateArrivalPolicyForm from "../components/BookingLinkConfig/Policies/LateArrivalPolicyForm";
import PaymentTermsForm from "../components/BookingLinkConfig/Policies/PaymentTermsForm";
import ServiceRequirementsForm from "../components/BookingLinkConfig/Policies/ServiceRequirementsForm";
import BookingApprovalForm from "../components/BookingLinkConfig/Settings/BookingApprovalForm";
import BufferSettingsForm from "../components/BookingLinkConfig/Settings/BufferSettingsForm";
import TravelFeeForm from "../components/BookingLinkConfig/Settings/TravelFeeForm";
import HideAddressForm from "../components/BookingLinkConfig/Settings/HideAddressForm";

// Map old tab URLs to section hash anchors for backward compat.
// The former "branding" tab was split into Business Identity + Personal
// Identity; both render together under the "Your Public Identity" group, so
// anchoring to "business-identity" lands the user with both in view.
const TAB_TO_HASH = {
  general: "booking-link",
  branding: "business-identity",
  policies: "cancellation",
  settings: "approval-scheduling",
};

const groups = [
  { id: "your-link", label: "Your Link" },
  { id: "public-identity", label: "Your Public Identity" },
  { id: "contact-social", label: "Contact & Social" },
  { id: "look-and-feel", label: "Look & Feel" },
  { id: "client-policies", label: "Client Policies" },
  { id: "how-bookings-work", label: "How Bookings Work" },
];

const sections = [
  {
    id: "booking-link",
    title: "Booking Link",
    description:
      "Set up your booking link and share it with clients to start accepting appointments.",
    group: "your-link",
    content: <BookingPageForm />,
    badge: "Required",
  },
  {
    id: "business-identity",
    title: "Business Identity",
    description:
      "Your banner, logo, and business name — how your business appears at the top of your booking page.",
    group: "public-identity",
    content: <BusinessIdentityForm />,
  },
  {
    id: "personal-identity",
    title: "Personal Identity",
    description:
      "Your name, photo, and a short bio so clients know who they're booking with.",
    group: "public-identity",
    content: <PersonalIdentityForm />,
  },
  {
    id: "contact-details",
    title: "Contact Details",
    description:
      "Set your contact details and address, and control their visibility on the booking page.",
    group: "contact-social",
    content: <ContactInformationForm />,
  },
  {
    id: "social-links",
    title: "Social Links",
    description:
      "Add your social media links and control their visibility on the booking page.",
    group: "contact-social",
    content: <SocialMediaForm />,
  },
  {
    id: "colors-theme",
    title: "Colors & Theme",
    description: "Choose your brand colors and visual style.",
    group: "look-and-feel",
    content: <VisualDesignForm />,
  },
  {
    id: "cancellation",
    title: "Cancellation",
    description:
      "Set your cancellation policy so clients know the rules before booking.",
    group: "client-policies",
    content: <CancellationPolicyForm />,
  },
  {
    id: "late-arrivals",
    title: "Late Arrivals",
    description:
      "Define your policy for handling late client arrivals and no-shows.",
    group: "client-policies",
    content: <LateArrivalPolicyForm />,
  },
  {
    id: "payment-terms",
    title: "Payment Terms",
    description:
      "Set your payment terms, deposit requirements, and accepted payment methods.",
    group: "client-policies",
    content: <PaymentTermsForm />,
  },
  {
    id: "preparation",
    title: "Preparation",
    description:
      "Specify any requirements or preparations clients need to make before your service.",
    group: "client-policies",
    content: <ServiceRequirementsForm />,
  },
  {
    id: "approval-scheduling",
    title: "Approval & Scheduling",
    description:
      "Choose whether bookings are automatically confirmed or require manual approval.",
    group: "how-bookings-work",
    content: <BookingApprovalForm />,
  },
  {
    id: "hide-in-person-address",
    title: "Location Address Privacy",
    description:
      "Control whether your location's street address is visible to clients before you confirm their booking.",
    group: "how-bookings-work",
    content: <HideAddressForm />,
  },
  {
    id: "buffer-time",
    title: "Buffer Time",
    description:
      "Set buffer time between appointments to account for travel and preparation.",
    group: "how-bookings-work",
    content: <BufferSettingsForm />,
  },
  {
    id: "travel-fees",
    title: "Travel Fees",
    description:
      "Configure travel fees for mobile services. Choose between fixed amounts or per-distance rates.",
    group: "how-bookings-work",
    content: <TravelFeeForm />,
  },
];

const BookingLinkConfigPage = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  // Backward compat: redirect old tab URLs to hash anchors
  useEffect(() => {
    if (tab && TAB_TO_HASH[tab]) {
      navigate(`/booking-page#${TAB_TO_HASH[tab]}`, { replace: true });
    } else if (tab) {
      // Unknown tab — redirect to base
      navigate("/booking-page", { replace: true });
    }
  }, [tab, navigate]);

  return (
    <SidebarSettingsLayout
      groups={groups}
      sections={sections}
    />
  );
};

export default BookingLinkConfigPage;
